import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { join, map, pipe, path } from 'ramda';

const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(3), maxWidth: 640 },
  pointer: { cursor: 'pointer' },
  underline: { textDecoration: 'underline' },
  zone: {
    minHeight: 130,
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '3px dashed',
    borderColor: theme.palette.primary.main,
    borderRadius: 5,
  },
  accepted: { borderColor: theme.palette.success.main },
  rejected: { borderColor: theme.palette.error.main },
}));

const fileAcceptanceProps = {
  accept:
    'application/pdf,' +
    'application/xml,' +
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  maxSize: '100000000',
};
const filesToNamesStr = pipe(map(path(['file', 'name'])), join(', '));

export const Dropzone = ({
  render = Function.prototype,
  name = '',
  register,
  single = false,
  disabled = false,
  attachment = false,
  maxSize = 100 * 1024 * 1024, // 100MB default
  inputProps = {},
  containerRef,
}) => {
  const [showClickAlert, setShowClickAlert] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const { features } = useTheme();
  const attachmentTypes = features && 'attachmentTypes' in features ? features.attachmentTypes : [];

  const mimeToLabel = {
    'application/pdf': 'PDF',
    'application/xml': 'XML',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  };

  const formattedTypes = (attachmentTypes.length > 0
    ? attachmentTypes.map((type) => mimeToLabel[type] || type)
    : ['PDF']
  ).join(', ');

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) =>
      render({
        acceptedFiles,
        namesRejectedFiles: filesToNamesStr(rejectedFiles),
      }),
    [render],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject, inputRef } = useDropzone({
    onDrop,
    accept: attachmentTypes.length > 0 ? attachmentTypes.join(',') : fileAcceptanceProps.accept,
    maxSize,
    disabled,
  });

  useEffect(() => {
    if (register) register({ ref: inputRef, name });
  }, [inputRef, name, register]);

  return (
    <div
      onMouseEnter={() => setShowClickAlert(true)}
      onMouseLeave={() => setShowClickAlert(false)}
      className={clsx(classes.root, showClickAlert && classes.pointer)}
      tabIndex={-1}
      ref={containerRef}
    >
      <div
        {...getRootProps({
          className: clsx([
            classes.zone,
            isDragAccept && classes.accepted,
            isDragReject && classes.rejected,
          ]),
        })}
      >
        <input {...getInputProps()} {...inputProps} />
        <Typography variant="body2" align="center" style={{ padding: '0 0.6rem' }}>
          {t('Drop document here or', { count: single ? 1 : 2 })}&nbsp;
          <span className={clsx(classes.highlight, showClickAlert && classes.underline)}>
            {t('select a file', { count: single ? 1 : 2 })}
          </span>
        </Typography>
        <Typography variant="body2" align="center">
          {attachment
            ? `(${formattedTypes}, max ${Math.floor(maxSize / (1024 * 1024))}MB)`
            : `(PDF, max ${Math.floor(maxSize / (1024 * 1024))}MB)`}
        </Typography>
      </div>
    </div>
  );
};
